import { useEffect, useCallback, useRef } from 'react';

export interface Shortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  action: () => void;
  description: string;
  category?: string;
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  preventDefault?: boolean;
}

/**
 * Hook for managing keyboard shortcuts.
 * Ignores shortcuts when focus is in inputs, textareas, or select elements.
 */
export function useKeyboardShortcuts(
  shortcuts: Shortcut[],
  options: UseKeyboardShortcutsOptions = {}
) {
  const { enabled = true, preventDefault = true } = options;
  const sequenceRef = useRef<string[]>([]);
  const sequenceTimerRef = useRef<number | null>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Ignore if typing in an input element
      const target = event.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      const isEditable = target.isContentEditable;

      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || isEditable) {
        // Only allow shortcuts with modifiers in input fields
        if (!event.ctrlKey && !event.metaKey && !event.altKey) {
          return;
        }
      }

      // Handle sequence shortcuts (e.g., 'g' then 'd')
      const key = event.key.toLowerCase();

      // Clear sequence after timeout
      if (sequenceTimerRef.current) {
        window.clearTimeout(sequenceTimerRef.current);
      }

      sequenceRef.current.push(key);
      sequenceTimerRef.current = window.setTimeout(() => {
        sequenceRef.current = [];
      }, 500);

      // Check for matching shortcuts
      for (const shortcut of shortcuts) {
        const shortcutKeys = shortcut.key.toLowerCase().split('+').map(k => k.trim());

        // Check for sequence match (e.g., "g d")
        if (shortcutKeys.length > 1 && !shortcutKeys.includes('ctrl') && !shortcutKeys.includes('shift') && !shortcutKeys.includes('alt') && !shortcutKeys.includes('meta')) {
          const sequence = sequenceRef.current.slice(-shortcutKeys.length);
          if (sequence.length === shortcutKeys.length &&
              sequence.every((k, i) => k === shortcutKeys[i])) {
            if (preventDefault) event.preventDefault();
            shortcut.action();
            sequenceRef.current = [];
            return;
          }
          continue;
        }

        // Check for single key with modifiers
        const expectedKey = shortcutKeys.find(k => !['ctrl', 'shift', 'alt', 'meta'].includes(k)) || '';
        const needsCtrl = shortcutKeys.includes('ctrl') || shortcut.ctrl;
        const needsShift = shortcutKeys.includes('shift') || shortcut.shift;
        const needsAlt = shortcutKeys.includes('alt') || shortcut.alt;
        const needsMeta = shortcutKeys.includes('meta') || shortcut.meta;

        if (
          key === expectedKey &&
          !!event.ctrlKey === !!needsCtrl &&
          !!event.shiftKey === !!needsShift &&
          !!event.altKey === !!needsAlt &&
          !!event.metaKey === !!needsMeta
        ) {
          if (preventDefault) event.preventDefault();
          shortcut.action();
          sequenceRef.current = [];
          return;
        }
      }
    },
    [shortcuts, enabled, preventDefault]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (sequenceTimerRef.current) {
        window.clearTimeout(sequenceTimerRef.current);
      }
    };
  }, [handleKeyDown, enabled]);

  return shortcuts;
}
